import React, { useCallback } from 'react'
import useExperiencePurchaseDetails from 'hooks/Experiences/useExperiencePurchaseDetails'
import TextButton from 'LuxKit/Button/TextButton'
import LineHotAirBalloonIcon from 'LuxKit/Icons/line/LineHotAirBalloonIcon'
import StatusLabel from 'LuxKit/Label/StatusLabel'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import CaptionTextBlock from 'LuxKit/TextBlocks/CaptionTextBlock'
import Group from 'components/utils/Group'
import LesaPurchaseSummaryContainer from './Common/LesaPurchaseSummaryContainer'
import LesaPurchaseSummaryHeader from './Common/LesaPurchaseSummaryHeader'
import LesaPurchaseSummarySeparator from './Common/LesaPurchaseSummarySeparator'
import BodyText from 'LuxKit/Typography/BodyText'
import Action from '../../Chat/MessageMeta/Action'
import { filterActions } from '../../../utils/actionsUtils'
import LesaPurchaseSummaryTitle from './Common/LesaPurchaseSummaryTitle'
import { addChatMessage } from 'actions/supportAssistant/supportAssistantActions'
import { useAppDispatch } from 'hooks/reduxHooks'

interface Props {
  order: App.Order;
  isPastOrder: boolean;
  hideSelect?: boolean;
  actions?: Array<App.ChatMessageMetaContent>;
  payload?: App.ChatMessageMetaPayload;
  componentsMap: Map<string | undefined, App.ChatMessageMetaContent>;
}

function LesaExperiencePurchaseSummary(props: Props) {
  const { order, isPastOrder, hideSelect, actions, payload, componentsMap } = props

  const dispatch = useAppDispatch()

  const {
    location,
    name,
    bookingNumber,
    experienceDate,
    experienceTime,
  } = useExperiencePurchaseDetails(order, isPastOrder)

  const orderActions = actions?.filter(action => filterActions(action, isPastOrder, ['listInclusions', 'showFinePrint', 'changeRoom']))

  const handleSelectClick = useCallback(() => {
    dispatch(addChatMessage('', { action: 'selectOrder', payload: { id_orders: order.id } }))
  }, [dispatch, order.id])

  return (
    <>
      {(!!componentsMap?.get('list-orders') || !!componentsMap?.get('selectOrder')) && (
        <LesaPurchaseSummaryContainer>
          <LesaPurchaseSummaryHeader>
            <BodyTextBlock lineClamp={2} variant="small" startIcon={<LineHotAirBalloonIcon size="XS" />}>Experience</BodyTextBlock>
            {!isPastOrder && <StatusLabel variant="confirmation">Upcoming</StatusLabel>}
            {isPastOrder && <StatusLabel variant="pending">Past</StatusLabel>}
          </LesaPurchaseSummaryHeader>
          <Group direction="vertical" tabletDirection="horizontal" horizontalAlign="space-between" verticalAlign="end" gap={16}>
            <Group direction="vertical" gap={4}>
              {location && <CaptionTextBlock
                variant="medium"
                colour="neutral-three"
              >{location}
              </CaptionTextBlock>}
              <LesaPurchaseSummaryTitle orderId={order.id}>
                {name}
              </LesaPurchaseSummaryTitle>{' '}
              <LesaPurchaseSummarySeparator>
                {experienceDate && <BodyText as="span" variant="small" colour="neutral-two">{experienceDate}</BodyText>}
                {experienceTime && <BodyText as="span" variant="small" colour="neutral-two">{experienceTime}</BodyText>}
              </LesaPurchaseSummarySeparator>
              {bookingNumber && <BodyTextBlock variant="small" colour="neutral-two"><strong>Booking ID:</strong> {bookingNumber}</BodyTextBlock>}{' '}
            </Group>
            {!hideSelect &&
              <TextButton onClick={handleSelectClick} fit="mobile-full-width" size="small" kind="secondary">
                Select
              </TextButton>
            }
          </Group>
        </LesaPurchaseSummaryContainer>
      )}
      {!!orderActions?.length &&
        <Group fullWidth wrap="wrap" horizontalAlign="start" direction="vertical" tabletDirection="horizontal" gap={8}>
          {orderActions.map((action) => (
            <Action key={action.action?.key} action={action} payload={payload} />
          ))}
        </Group>}
    </>
  )
}

export default LesaExperiencePurchaseSummary
